// Field types for the query builder
export type CandidateFieldType = 'text' | 'array' | 'enum' | 'date' | 'reference' | 'language';

export interface CandidateFieldDefinition {
	key: string;
	label: string;
	type: CandidateFieldType;
	options?: string[];
}

// All available candidate fields for querying
export const CANDIDATE_FIELDS: CandidateFieldDefinition[] = [
	{ key: 'name', label: 'Name', type: 'text' },
	{ key: 'email', label: 'Email', type: 'text' },
	{ key: 'phone', label: 'Phone', type: 'text' },
	{ key: 'function', label: 'Function', type: 'text' },
	{ key: 'location', label: 'Location', type: 'text' },
	{ key: 'linkedin', label: 'LinkedIn', type: 'text' },
	{ key: 'skills', label: 'Skills', type: 'array' },
	{ key: 'status', label: 'Status', type: 'enum', options: ['looking', 'open', 'unavailable', 'hired'] },
	{ key: 'created_at', label: 'Created At', type: 'date' },
	{ key: 'updated_at', label: 'Updated At', type: 'date' },
	{ key: 'manager', label: 'Manager', type: 'reference' },
	{ key: 'language', label: 'Language', type: 'language' },
];

// Operators by field type
type TextOperator = 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'is_empty' | 'is_not_empty';
type ArrayOperator = 'contains_any' | 'contains_all' | 'not_contains' | 'is_empty' | 'is_not_empty';
type EnumOperator = 'equals' | 'not_equals' | 'in' | 'not_in';
type DateOperator = 'equals' | 'before' | 'after' | 'between' | 'is_empty' | 'is_not_empty';
type ReferenceOperator = 'equals' | 'not_equals' | 'is_empty' | 'is_not_empty';
type LanguageOperator = 'any_level' | 'at_least' | 'at_most' | 'exactly';

export type Operator = TextOperator | ArrayOperator | EnumOperator | DateOperator | ReferenceOperator | LanguageOperator;

export interface OperatorDefinition {
	key: Operator;
	label: string;
	requiresValue: boolean;
	requiresSecondValue?: boolean;
}

// Operator definitions grouped by field type
const TEXT_OPERATORS: OperatorDefinition[] = [
	{ key: 'equals', label: 'equals', requiresValue: true },
	{ key: 'not_equals', label: 'does not equal', requiresValue: true },
	{ key: 'contains', label: 'contains', requiresValue: true },
	{ key: 'not_contains', label: 'does not contain', requiresValue: true },
	{ key: 'starts_with', label: 'starts with', requiresValue: true },
	{ key: 'ends_with', label: 'ends with', requiresValue: true },
	{ key: 'is_empty', label: 'is empty', requiresValue: false },
	{ key: 'is_not_empty', label: 'is not empty', requiresValue: false },
];

const ARRAY_OPERATORS: OperatorDefinition[] = [
	{ key: 'contains_any', label: 'contains any of', requiresValue: true },
	{ key: 'contains_all', label: 'contains all of', requiresValue: true },
	{ key: 'not_contains', label: 'does not contain', requiresValue: true },
	{ key: 'is_empty', label: 'is empty', requiresValue: false },
	{ key: 'is_not_empty', label: 'is not empty', requiresValue: false },
];

const ENUM_OPERATORS: OperatorDefinition[] = [
	{ key: 'equals', label: 'equals', requiresValue: true },
	{ key: 'not_equals', label: 'does not equal', requiresValue: true },
	{ key: 'in', label: 'is one of', requiresValue: true },
	{ key: 'not_in', label: 'is not one of', requiresValue: true },
];

const DATE_OPERATORS: OperatorDefinition[] = [
	{ key: 'equals', label: 'equals', requiresValue: true },
	{ key: 'before', label: 'is before', requiresValue: true },
	{ key: 'after', label: 'is after', requiresValue: true },
	{ key: 'between', label: 'is between', requiresValue: true, requiresSecondValue: true },
	{ key: 'is_empty', label: 'is empty', requiresValue: false },
	{ key: 'is_not_empty', label: 'is not empty', requiresValue: false },
];

const REFERENCE_OPERATORS: OperatorDefinition[] = [
	{ key: 'equals', label: 'equals', requiresValue: true },
	{ key: 'not_equals', label: 'does not equal', requiresValue: true },
	{ key: 'is_empty', label: 'is empty', requiresValue: false },
	{ key: 'is_not_empty', label: 'is not empty', requiresValue: false },
];

const LANGUAGE_OPERATORS: OperatorDefinition[] = [
	{ key: 'any_level', label: 'at any level', requiresValue: false },
	{ key: 'at_least', label: 'at least', requiresValue: true },
	{ key: 'at_most', label: 'at most', requiresValue: true },
	{ key: 'exactly', label: 'exactly', requiresValue: true },
];

// Query condition
export interface QueryCondition {
	id: string;
	field: string;
	operator: Operator;
	value: string | string[] | number | boolean | null;
	secondValue?: string | null;
}

// Query group (can contain conditions or nested groups)
export interface QueryGroup {
	id: string;
	type: 'AND' | 'OR';
	conditions: (QueryCondition | QueryGroup)[];
}

// Type guards
export function isQueryGroup(item: QueryCondition | QueryGroup): item is QueryGroup {
	return 'type' in item && 'conditions' in item;
}

export function isQueryCondition(item: QueryCondition | QueryGroup): item is QueryCondition {
	return 'field' in item && 'operator' in item;
}

// Get operators for a field type
export function getOperatorsForFieldType(type: CandidateFieldType): OperatorDefinition[] {
	switch (type) {
		case 'text': return TEXT_OPERATORS;
		case 'array': return ARRAY_OPERATORS;
		case 'enum': return ENUM_OPERATORS;
		case 'date': return DATE_OPERATORS;
		case 'reference': return REFERENCE_OPERATORS;
		case 'language': return LANGUAGE_OPERATORS;
		default: return TEXT_OPERATORS;
	}
}

// Get field definition by key
export function getFieldDefinition(fieldKey: string): CandidateFieldDefinition | undefined {
	return CANDIDATE_FIELDS.find(f => f.key === fieldKey);
}

// Generate unique ID
export function generateId(): string {
	return crypto.randomUUID();
}

// Create empty group
export function createEmptyGroup(type: 'AND' | 'OR' = 'AND'): QueryGroup {
	return {
		id: generateId(),
		type,
		conditions: []
	};
}

// Create empty condition
export function createEmptyCondition(): QueryCondition {
	return {
		id: generateId(),
		field: 'name',
		operator: 'contains',
		value: ''
	};
}
